import { computed, makeObservable } from 'mobx';
import { JobMapping } from '../../pages/private/devices/devices-inventory/utils';
import { formatDate } from 'common-utils/date-utils';
import { IDeviceDetailsConstructorOptions } from './device-detail-entry-store';
import { HistoricalListingStore } from './historical-listing.store';

enum DEPLOYMENT_STATUS_DETAIL {
    success = 'SUCCESS',
    downloaded = 'DOWNLOADED',
    failure = 'ERROR',
    pending_reinstall = 'PENDING-REINSTALL',
    pending_remove = 'PENDING-REMOVE'
}

export interface IProcessProps {
    device_id: string;
    install_result: any[];
    job_id: string;
    external_update: boolean;
    packages: any[];
    remove_result: any[];
    sourcelists: any[];
    type: string;
    update_result: any[];
    reconfigure_result: any[];
}

export interface IDisplayDeploymentProps
    extends Omit<IOuterProcessListingProps, 'software_updates_listing' | 'snapshots'> {
    key: string;
    jobId: string;
    jobName: string;
    status: string;
    displayTimestamp: string;
    deploymentLogDataRecords: IDeploymentDataRecord[];
    snapshots: any[];
}

export interface IOuterProcessListingProps {
    jobName: string;
    timestamp: string;
    software_updates_listing: IProcessProps;
    snapshots?: any[];
}

export interface IDeploymentDataRecord {
    name: string;
    error: string;
    notice: string;
    output: string;
    warning: string;
}

export class DeviceDeploymentsStore extends HistoricalListingStore<IOuterProcessListingProps> {
    constructor(options: IDeviceDetailsConstructorOptions) {
        super('deployment', options);
        makeObservable(this);
    }

    /** *************************************/
    @computed
    public get displayDeployments(): IDisplayDeploymentProps[] {
        if (!this.entities) return [];
        return this.entities.map<IDisplayDeploymentProps>((item) => {
            const displayedDeploymentDataResult: IDeploymentDataRecord[] = [];
            const parseResult = (name: string, result: any): IDeploymentDataRecord => ({
                name,
                error: result.error?.join('\n'),
                notice: result.notice?.join('\n'),
                warning: result.warning?.join('\n'),
                output: result.output.replace(/(?:\r)/g, '\n'),
            });

            item?.software_updates_listing?.install_result && displayedDeploymentDataResult.push(parseResult('install', item.software_updates_listing.install_result));
            item?.software_updates_listing?.remove_result && displayedDeploymentDataResult.push(parseResult('remove', item.software_updates_listing.remove_result));
            item?.software_updates_listing?.update_result && displayedDeploymentDataResult.push(parseResult('update', item.software_updates_listing.update_result));
            item?.software_updates_listing?.reconfigure_result && displayedDeploymentDataResult.push(parseResult('reconfigure', item.software_updates_listing.reconfigure_result));

            return {
                key: `${item.software_updates_listing.job_id}-${item.timestamp}`,
                jobName: item.software_updates_listing.external_update ? 'External software update' : (item.jobName ? item.jobName : JobMapping.software_update),
                jobId: item.software_updates_listing.job_id,
                status: DEPLOYMENT_STATUS_DETAIL[item.software_updates_listing.type] || DEPLOYMENT_STATUS_DETAIL.failure,
                displayTimestamp: formatDate(item.timestamp),
                timestamp: item.timestamp,
                deploymentLogDataRecords: displayedDeploymentDataResult,
                snapshots: item.snapshots,
            };
        }).sort((prev, next) => (
            prev.timestamp < next.timestamp ? 1 : prev.timestamp > next.timestamp ? -1 : 0
        ));
    }

    /** ************************************
     * Override functions
     **************************************/
    protected getHistoricalEntries(accountId: number, uid: string, filterByDateFrom: string, filterByDateTo: string): Promise<any[]> {
        return this._devicesStore.getDeviceSoftwareUpdates(accountId, uid, filterByDateFrom, filterByDateTo);
    }
}
